// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface Props {
  npc: any // GeneratorResult — loosely typed until all field access is tightened
  onSave: () => Promise<string | null>
  onRegenerate: () => void
  onClear: () => void
  error: string | null
}

export function NPCPreviewPanel({ npc, onSave, onRegenerate, onClear, error }: Props) {
  const attrs = npc.attributes ?? {}
  const pp = npc.physicalProfile ?? {}
  const livingChildren = (npc.children ?? []).filter((c: { status: string }) => c.status === 'alive')
  const livingSpouses = (npc.spouses ?? []).filter((s: { status: string }) => s.status === 'alive')

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-accent">
        {npc.name ?? 'Unnamed NPC'}
      </h3>

      {error && <p className="text-sm text-danger bg-red-50 p-2 rounded">{error}</p>}

      {/* Identity */}
      <div className="bg-white border border-parchment-dark rounded p-3">
        <div className="grid grid-cols-2 gap-1 text-sm">
          <div><span className="font-semibold">Class:</span> {npc.socialClass}</div>
          <div><span className="font-semibold">Sex:</span> {npc.sex}</div>
          <div><span className="font-semibold">Age:</span> {npc.age}</div>
          <div><span className="font-semibold">Phase:</span> {npc.phase}</div>
          <div><span className="font-semibold">Occupation:</span> {npc.occupation?.label ?? npc.occupation?.id ?? '—'}</div>
          <div><span className="font-semibold">Sunsign:</span> {npc.sunsign}</div>
          <div><span className="font-semibold">Deity:</span> {npc.publicDeity ?? 'None'}</div>
          <div><span className="font-semibold">Morality:</span> {npc.morality} ({npc.moralityBand})</div>
          <div><span className="font-semibold">Wealth:</span> {npc.wealthLevel} ({npc.wealthScore})</div>
          {npc.isSecretWorshipper && (
            <div className="col-span-2 text-danger font-semibold">
              Secret: {npc.secretDeity}{npc.tashalCell ? ' (Tashal cell)' : ''}
            </div>
          )}
        </div>
      </div>

      {/* Personality */}
      {npc.personality && (
        <div className="bg-white border border-parchment-dark rounded p-3">
          <div className="text-sm font-semibold mb-1">Personality</div>
          <div className="text-sm italic">{npc.personality}</div>
          {npc.oceanScores && (
            <div className="mt-2 grid grid-cols-5 gap-1 text-xs text-center">
              {['O', 'C', 'E', 'A', 'N'].map(dim => (
                <div key={dim}>
                  <div className="font-semibold">{dim}</div>
                  <div className="bg-parchment-dark rounded h-1.5 mt-0.5">
                    <div className="bg-accent rounded h-1.5" style={{ width: `${npc.oceanScores[dim]}%` }} />
                  </div>
                  <div className="mt-0.5">{npc.oceanScores[dim]}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Attributes */}
      <div className="bg-white border border-parchment-dark rounded p-3">
        <div className="text-sm font-semibold mb-1">Attributes</div>
        <div className="grid grid-cols-4 gap-1 text-xs">
          {['STR','STA','DEX','AGL','EYE','HRG','SML','VOI','INT','AUR','WIL','CML'].map(a => (
            <div key={a}><span className="font-semibold">{a}</span> {attrs[a] ?? '—'}</div>
          ))}
        </div>
      </div>

      {/* Physical */}
      {pp.frame && (
        <div className="bg-white border border-parchment-dark rounded p-3 text-sm">
          <div className="font-semibold mb-1">Physical</div>
          <div className="grid grid-cols-2 gap-1 text-xs">
            <div>Height: {pp.heightInches ? `${Math.floor(pp.heightInches/12)}'${pp.heightInches%12}"` : '—'}</div>
            <div>Frame: {pp.frame}</div>
            <div>Weight: {pp.weightLbs ? `${pp.weightLbs} lb` : '—'}</div>
            <div>Complexion: {pp.complexion}</div>
            <div>Hair: {pp.hairColour} {pp.hairLength ? `(${pp.hairLength})` : ''}</div>
            <div>Eyes: {pp.eyeColour}</div>
          </div>
          {pp.marks?.length > 0 && (
            <div className="mt-1 text-xs text-ink-light">{pp.marks.join(', ')}</div>
          )}
        </div>
      )}

      {/* Appearance Descriptions — only show AI-generated, not templates */}
      {npc.appearance?._source === 'claude' && (
        <div className="bg-white border border-parchment-dark rounded p-3 text-sm">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-semibold">Appearance</span>
            {npc.appearance._source && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                npc.appearance._source === 'claude' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
              }`}>
                {npc.appearance._source === 'claude' ? 'AI' : 'template'}
              </span>
            )}
          </div>
          {['travelling', 'at_work', 'leisure', 'formal'].map((ctx: string) => {
            const desc = npc.appearance[ctx]
            if (!desc) return null
            const label = ctx === 'at_work' ? 'At Work' : ctx.charAt(0).toUpperCase() + ctx.slice(1)
            return (
              <div key={ctx} className="mb-2">
                <div className="text-xs font-semibold text-ink-light">{label}</div>
                <p className="text-xs italic leading-relaxed">{desc}</p>
              </div>
            )
          })}
        </div>
      )}

      {/* Family Summary */}
      <div className="bg-white border border-parchment-dark rounded p-3 text-sm">
        <div className="font-semibold mb-1">Family</div>
        <div className="text-xs space-y-0.5">
          <div>Parents: {(npc.parents ?? []).length}</div>
          <div>Siblings: {(npc.siblings ?? []).length}</div>
          <div>Spouses: {livingSpouses.length} living</div>
          <div>Children: {livingChildren.length} living of {(npc.children ?? []).length} total</div>
          <div>Contacts: {(npc.contacts ?? []).length}</div>
        </div>
      </div>

      {/* History Summary */}
      <div className="bg-white border border-parchment-dark rounded p-3 text-sm">
        <div className="font-semibold mb-1">Life History ({(npc.history ?? []).length} events)</div>
        <div className="text-xs space-y-0.5 max-h-40 overflow-y-auto">
          {(npc.history ?? [])
            .filter((h: { eventId: string }) => h.eventId !== 'quiet_year')
            .slice(0, 15)
            .map((h: { age: number; eventLabel: string; label?: string; year: number }, i: number) => (
              <div key={i} className="text-ink-light">
                <span className="font-medium text-ink">Age {h.age}</span> ({h.year} TR) — {h.eventLabel ?? h.label}
              </div>
            ))}
          {(npc.history ?? []).filter((h: { eventId: string }) => h.eventId !== 'quiet_year').length > 15 && (
            <div className="text-ink-light italic">...and more</div>
          )}
        </div>
      </div>

      {/* Conditions */}
      {(npc.conditions?.length ?? 0) > 0 && (
        <div className="bg-white border border-parchment-dark rounded p-3 text-sm">
          <div className="font-semibold mb-1">Conditions</div>
          <div className="flex flex-wrap gap-1">
            {npc.conditions!.map((c: string) => (
              <span key={c} className="px-2 py-0.5 bg-parchment-dark rounded text-xs">{c}</span>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button onClick={onSave}
          className="flex-1 py-2 bg-success text-white rounded hover:opacity-90 font-medium">
          Save to Campaign
        </button>
        <button onClick={onRegenerate}
          className="flex-1 py-2 bg-accent text-white rounded hover:bg-accent-light font-medium">
          Regenerate
        </button>
        <button onClick={onClear}
          className="px-4 py-2 border border-parchment-dark rounded hover:bg-parchment-dark text-sm">
          Clear
        </button>
      </div>
    </div>
  )
}
